import { Box } from '@mui/material';
import { GridActionsCellItemProps, GridRowParams } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { EmptyState, P1M, P1R, TableAdvancedMetaColumn, usersColumnVisibilityModel, UsersTable } from 'src/components';
import { useModal } from 'src/hooks';
import { deletePendingTenantUsers, resendInviteTenantUsers } from 'src/redux/tenants/actions';
import { selectCurrTenantId } from 'src/redux/tenants/selectors';
import { themeColors } from 'src/theme';
import { UserType } from 'src/types';
import { formatTimestamp } from 'src/utils';
import { useTranslation } from 'react-i18next';

import StyledActionItem from 'src/components/Table/TableComponents/StyledActionItem';
import trashIcon from 'src/assets/trashRedIcon.svg';
import { InvitationInfoType } from '..';

type PendingUsersTabProps = {
  users: UserType[];
  setInviteInfo: ({ email, role, isResendEmail }: InvitationInfoType) => void;
};

const PendingUsersTab = ({ users, setInviteInfo }: PendingUsersTabProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currTenantId = useSelector(selectCurrTenantId);
  const { confirm, context, destroy } = useModal();

  const columnsVisibility = {
    ...usersColumnVisibilityModel,
    email: true,
    userRoles: true,
    phone: true,
  };

  const onResendInviteClick = (user: Partial<UserType>) => {
    setInviteInfo({ email: user.email || '', role: user?.roles?.[0]?.slug || '', isResendEmail: true });
    dispatch(resendInviteTenantUsers.init({ id: currTenantId, uuid: user.uuid }));
  };

  const handleDeleteUser = (user: Partial<UserType>) => {
    if (user) {
      dispatch(deletePendingTenantUsers.init({ id: currTenantId, uuid: user.uuid }));
      destroy();
    }
  };

  const onDeleteUserClick = (user: Partial<UserType>) => {
    confirm({
      icon: trashIcon,
      title: 'Are You Sure?',
      subTitle: (
        <P1R>
          You are about to delete user
          <P1M> {user.email}</P1M>
        </P1R>
      ),
      onOk: () => handleDeleteUser(user),
      okText: t('eventPage.yesDelete'),
      theme: 'delete',
    });
  };

  const getActions = (params: GridRowParams<any>): React.ReactElement<GridActionsCellItemProps>[] => {
    const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

    actions.push(<StyledActionItem key="resendInvite" label="Resend Invite" onClick={() => onResendInviteClick(params.row)} showInMenu />);

    actions.push(
      <StyledActionItem
        sx={{ color: themeColors.red }}
        key="deleteCell"
        label="Delete"
        onClick={() => onDeleteUserClick(params.row)}
        color={themeColors.red}
        showInMenu
      />,
    );

    return actions;
  };

  const metaColumns: TableAdvancedMetaColumn[] = [
    {
      index: 98,
      column: {
        field: 'invitedBy',
        headerName: 'invited By',
        width: 140,
        renderCell: (params) => {
          return params.row.invitedBy;
        },
      },
    },
    {
      index: 99,
      column: {
        field: 'invitationLinkValid',
        headerName: 'invitation link valid to',
        minWidth: 180,
        headerAlign: 'center',
        flex: 1,
        renderCell: (params) => {
          const formattedDate = formatTimestamp(params.row.verificationCodeExpiration);
          const isExpired = dayjs(params.row.verificationCodeExpiration).isBefore(dayjs());

          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isExpired ? themeColors.redLight : 'inherit',
                color: isExpired ? themeColors.red : 'inherit',
              }}
            >
              {formattedDate}
            </Box>
          );
        },
      },
    },
  ];

  return (
    <Box>
      <Box component="main" sx={{ height: 'calc(100vh - 150px)', pt: 4 }}>
        {users?.length ? (
          <UsersTable
            statePersistId="user_management_pending_table"
            users={users}
            metaColumns={metaColumns}
            columnVisibilityModel={columnsVisibility}
            getActions={getActions}
            toolbarProps={{ options: 'mini' }}
            disableVirtualization
            usePaginaionParams
          />
        ) : (
          <EmptyState type="people" title={t('usersPage.noUsers')} />
        )}
      </Box>

      {context}
    </Box>
  );
};

export default PendingUsersTab;
